import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

/**
 * Whether the AI assistant is available to the signed-in user (#860).
 *
 * The server decides: its key must be configured AND the user's tenant must have
 * `ai_assistant_enabled` on (GET /api/v1/assistant/status). This hook only reads
 * that answer, and it FAILS CLOSED — loading, signed out, an error, or anything but
 * an explicit `enabled: true` all mean "don't render the assistant". The button
 * must never flash in before the answer arrives.
 *
 * The status call sits outside the chat rate limit (#859), so asking on every page
 * load costs nothing. Five minutes stale means an owner's switch shows up for
 * already-open sessions without a reload.
 */
export const ASSISTANT_STATUS_STALE_MS = 5 * 60 * 1000;

export function useAssistantEnabled(): boolean {
  const userId = useAuthStore((s) => s.user?.user_id);
  const { data } = useQuery({
    queryKey: ['assistant', 'status', userId] as const,
    queryFn: () => api.get<{ enabled?: unknown }>('/v1/assistant/status').then((r) => r.data),
    enabled: !!userId,
    staleTime: ASSISTANT_STATUS_STALE_MS,
    retry: false,
  });
  return data?.enabled === true;
}
